'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ChevronRight, ArrowLeft, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  showBackButton?: boolean;
  backHref?: string;
  /**
   * Label shown next to the back arrow in 'back-only' variant.
   * e.g. "Back to Home", "Back to Dashboard" * Defaults to"Back" if not provided.
   */
  backLabel?: string;
  className?: string;
  /**
   * 'back-only'  — shows only the back arrow + destination label (patient-facing pages, reduces clutter)
   * 'full'       — shows back button + full breadcrumb trail (complex portals)
   * default: 'full'
   */
  variant?: 'back-only' | 'full';
}

export default function Breadcrumb({
  items,
  showBackButton = true,
  backHref,
  backLabel,
  className = '',
  variant = 'full',
}: BreadcrumbProps) {
  const router = useRouter();

  const handleBack = () => {
    if (backHref) {
      router.push(backHref);
    } else {
      router.back();
    }
  };

  // Derive a contextual back label from backHref if not explicitly provided
  const resolvedBackLabel = backLabel ?? (
    backHref === '/' ? 'Back to Home'
    : backHref === '/eeg-cognitive-dashboard' ? 'Back to Dashboard'
    : backHref === '/patient-dashboard' ? 'Back to Dashboard'
    : backHref === '/clinician-portal' ? 'Back to Portal'
    : backHref ? `Back to ${items[0]?.label ?? 'previous page'}`
    : 'Back'
  );

  // ── Back-only variant (patient-facing pages) ──────────────────────────────
  if (variant === 'back-only') {
    return (
      <nav aria-label="Go back" className={`flex items-center ${className}`}>
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors group"
          aria-label={resolvedBackLabel}
        >
          <span className="flex items-center justify-center w-7 h-7 rounded-lg border border-[#1E3A5F]/60 bg-[#0B1628]/60 text-[#8BAFD4] group-hover:text-white group-hover:border-[#F97316]/40 group-hover:bg-[#0F1E35] transition-all duration-150 active:scale-95">
            <ArrowLeft size={13} aria-hidden="true" />
          </span>
          <span className="group-hover:text-zinc-300">{resolvedBackLabel}</span>
        </button>
      </nav>
    );
  }

  // ── Full breadcrumb variant (complex portals) ─────────────────────────────
  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-2 flex-wrap ${className}`}
    >
      {showBackButton && (
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#1E3A5F]/60 bg-[#0B1628]/60 text-[#8BAFD4] hover:text-white hover:border-[#F97316]/40 hover:bg-[#0F1E35] transition-all duration-150 active:scale-95 touch-manipulation flex-shrink-0 mr-1"
          aria-label="Go back"
          title="Go back"
        >
          <ArrowLeft size={14} aria-hidden="true" />
        </button>
      )}

      <ol className="flex items-center gap-1 flex-wrap" aria-label="Breadcrumb navigation">
        <li>
          <Link
            href="/"
            className="flex items-center text-zinc-500 hover:text-zinc-300 transition-colors text-[11px]"
            aria-label="Home"
          >
            <Home size={12} aria-hidden="true" />
          </Link>
        </li>

        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <React.Fragment key={index}>
              <li aria-hidden="true">
                <ChevronRight size={10} className="text-zinc-700" />
              </li>
              <li>
                {isLast || !item.href ? (
                  <span
                    className={`text-[11px] font-medium ${isLast ? 'text-zinc-300' : 'text-zinc-500'}`}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    className="text-[11px] font-medium text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
